<script setup lang="ts">
import { BModal } from 'bootstrap-vue-next';
import ButtonModalClose from "@buttons/button-modal-close.vue";
import PartAppThemeSwitcher from '@parts/part-app-theme-switcher.vue';
import FormSelect from '@form/form-select.vue';
import ButtonNavigationLink from '@buttons/button-navigation-link.vue';
import PartNavigationList from '@parts/part-navigation-list.vue';

import {useCommonStore} from '@store/common';
import { computed } from 'vue';
import {useUserStore} from '@store/user';
import {storeToRefs} from 'pinia';
import {useCode} from "@composables/code";
import {useI18n} from 'vue-i18n';
import {useAuthApi} from "@store/api/authApi";

const {t} = useI18n();
const { logout } = useAuthApi().methods;

const props = defineProps({
	id: {
		type: String,
		default: 'modal-mobile-menu'
	}
});

const userStore = useUserStore();
const commonStore = useCommonStore();
const {user} = storeToRefs(userStore);
const { languages, mainMenu } = storeToRefs(commonStore);
const account = computed(() => ({
	list: [
		{
			title: `ID: ${user.value?.id}`,
			icon: {
				name: "account",
				viewBox: "0 0 18 18"
			}
		}
	]
}));

const { copyToClipboard } = useCode();

const languagesSelectList = computed(() => {
	return languages.value.map((lang) => ({
		label: lang?.name,
		value: lang.locale,
	}));
});

</script>

<template>
	<BModal
		:id="id"
		no-trap
		no-header-close
		centered
		footer-class="modal-mobile-menu__footer"
		content-class="modal-mobile-menu__content"
		class="modal-mobile-menu"
	>
		<template #header="{ close }">
			<button-modal-close
				class="modal-mobile-menu__close"
				@click="close"
			/>
		</template>

		<template #default="{ close }">
			<part-navigation-list
				v-bind="account"
				transparent-item
				class="modal-mobile-menu__account"
				@click="copyToClipboard(`${user?.id}`)"
			/>

			<ul v-if="mainMenu.length > 0" class="modal-mobile-menu__menu">
				<li
					v-for="(item, index) in mainMenu" :key="index"
					class="modal-mobile-menu__menu-item"
				>
					<button-navigation-link
						v-bind="item"
						@click="close"
						class="modal-mobile-menu__menu-link"
					/>
				</li>
				<li class="modal-mobile-menu__menu-item">
					<button-navigation-link
						:icon="{ name: 'logout', viewBox: '0 0 18 18' }"
						:title="t('common.buttons.logout')"
						class="modal-mobile-menu__logout"
						@click.prevent="logout(); close()"
					/>
				</li>
			</ul>
		</template>

		<template #footer>
			<part-app-theme-switcher class="modal-mobile-menu__theme" />
			<form-select
				name="lang"
				to-top
				:icon="{ name: 'website', viewBox: '0 0 25 24' }"
				:list="languagesSelectList"
				v-model="user.lang"
				class="modal-mobile-menu__lang"
			/>
		</template>
	</BModal>
</template>

<style lang="scss">
.modal-mobile-menu {
	@include gray-modal;
	&.modal {
		--bs-modal-color: rgb(--color-text-rgb);
	}

	&__close {
		position: absolute;
		width: 2.4rem;
		height: 2.4rem;
		top: 1.2rem;
		right: 1.2rem;
		z-index: 10;
		fill: rgba(var(--color-text-rgb), 0.25);
	}

	&__footer {
		display: flex;
		justify-content: space-between;
		gap: 2rem;
		padding: 1.6rem;
	}

	&__content {
		min-height: 55.8rem;
	}

	&__menu {
		display: flex;
		flex-direction: column;
		gap: 0.6rem;
	}

	&__menu-item {
		display: block;
	}
	&__menu-link {
		width: 100%;
	}

	&__account {
		@include font-16-pro-regular;
		margin-bottom: 0.6rem;
	}

	&__theme {
		@include theme-dark {
			background: rgba(var(--color-active-rgb), 0.06);
			border: 1px solid rgba(var(--color-active-rgb), 0.06);
		}
	}
}
</style>
